import { useState, useEffect, useMemo } from 'react';
import {
  Globe, RefreshCw, Plus, Trash2, X, Search, Filter,
  Package, ChevronDown, CheckCircle, AlertTriangle, Clock,
  ArrowRight, Tag, FileType, Server, Loader2,
} from 'lucide-react';
import axios from 'axios';
import apiClient from '../services/api';
import useStore from '../store/useStore';
import { getActiveProfile, getProfiles } from '../utils/connectorConfig';
import { formatDate, formatTime } from '../utils/dateFormat';

// ── Negotiation modal ─────────────────────────────────────────────────────────
function NegotiateModal({ offer, onClose }) {
  const { connectorKey } = useStore();

  // Build list of all profiles that have a consumer URL — user picks which one to negotiate from
  const consumerProfiles = useMemo(() => {
    const all = getProfiles();
    return all.filter(p => p.consumerEdcUrl);
  }, [connectorKey]);

  const defaultProfile = useMemo(() => {
    const active = getActiveProfile();
    if (active?.consumerEdcUrl) return active;
    return consumerProfiles[0] || null;
  }, [connectorKey, consumerProfiles]);

  const [selectedProfileId, setSelectedProfileId] = useState(() => defaultProfile?.id || '');
  const selectedProfile = useMemo(
    () => consumerProfiles.find(p => p.id === selectedProfileId) || defaultProfile,
    [selectedProfileId, consumerProfiles, defaultProfile]
  );

  const [step, setStep] = useState('idle');
  const [negotiationId, setNegotiationId] = useState(null);
  const [agreementId, setAgreementId] = useState(null);
  const [transferId, setTransferId] = useState(null);
  const [errorMsg, setErrorMsg] = useState('');
  const [destination, setDestination] = useState('');

  const token = localStorage.getItem('kc_token');
  const authHeaders = token ? { Authorization: `Bearer ${token}` } : {};
  const consumerUrl = selectedProfile?.consumerEdcUrl || '';
  const apiKey = selectedProfile?.consumerApiKey || selectedProfile?.providerApiKey || '';
  const edcHeaders = apiKey ? { 'X-Api-Key': apiKey } : {};

  const proxy = (path, method = 'GET', data = undefined) =>
    axios.post('/api/v1/edc-proxy', {
      targetUrl: consumerUrl,
      path,
      method,
      data,
      headers: edcHeaders,
    }, { headers: authHeaders });

  const startNegotiation = async () => {
    if (!consumerUrl) {
      setErrorMsg('No consumer connector with a consumer EDC URL found. Please configure one first.');
      setStep('error');
      return;
    }
    setStep('negotiating');
    setErrorMsg('');
    apiClient.post(`/data-pool/offers/${offer.id}/request`).catch(() => { /* best-effort */ });
    try {
      let freshOfferId = offer.offer_id;
      let freshPolicy = null;
      let freshParticipantId = offer.provider_participant_id;
      try { freshPolicy = JSON.parse(offer.policy || 'null'); } catch { /* ignore */ }

      // Fetch fresh catalog via standard v2 API to get valid, non-stale offer data
      try {
        const catRes = await proxy(
          '/api/management/v2/catalog/request',
          'POST',
          {
            '@context': {},
            counterPartyAddress: offer.dsp_endpoint,
            protocol: 'dataspace-protocol-http',
          }
        );
        // Try sovity wrapper response first
        const dataOffer = catRes.data?.dataOffers?.find(o => o.assetId === offer.asset_id);
        if (dataOffer?.contractOffers?.[0]) {
          freshOfferId = dataOffer.contractOffers[0].contractOfferId || freshOfferId;
          freshPolicy = dataOffer.contractOffers[0].policy || freshPolicy;
          freshParticipantId = dataOffer.participantId || freshParticipantId;
        } else {
          // Standard EDC dcat:Dataset array
          const datasets = catRes.data?.['dcat:dataset'] || (Array.isArray(catRes.data) ? catRes.data : []);
          const dsList = Array.isArray(datasets) ? datasets : [datasets];
          const ds = dsList.find(d => (d['@id'] || d['edc:id']) === offer.asset_id);
          if (ds) {
            const pol = ds['odrl:hasPolicy'];
            const polItem = Array.isArray(pol) ? pol[0] : pol;
            if (polItem?.['@id']) freshOfferId = polItem['@id'];
            if (polItem) freshPolicy = polItem;
          }
        }
      } catch { /* fall through to cached data */ }

      const body = {
        '@context': {
          'edc': 'https://w3id.org/edc/v0.0.1/ns/',
          'odrl': 'http://www.w3.org/ns/odrl/2/',
        },
        '@type': 'NegotiationInitiateRequestDto',
        connectorAddress: offer.dsp_endpoint,
        protocol: 'dataspace-protocol-http',
        ...(freshParticipantId ? { connectorId: freshParticipantId } : {}),
        offer: {
          offerId: freshOfferId || `${offer.asset_id}:${offer.asset_id}:1`,
          assetId: offer.asset_id,
          policy: freshPolicy || {
            '@context': { odrl: 'http://www.w3.org/ns/odrl/2/' },
            '@type': 'odrl:Offer',
            '@id': freshOfferId || `offer-${offer.asset_id}`,
            'odrl:target': { '@id': offer.asset_id },
            'odrl:permission': [{ 'odrl:action': { '@id': 'odrl:use' } }],
            'odrl:prohibition': [],
            'odrl:obligation': [],
          },
        },
      };

      const res = await proxy('/api/management/v2/contractnegotiations', 'POST', body);
      const negId = res.data?.['@id'] || res.data?.id;
      if (!negId) throw new Error('No negotiation ID in response');
      setNegotiationId(negId);
      setStep('polling');
      pollNegotiation(negId);
    } catch (err) {
      setErrorMsg(err.response?.data?.[0]?.message || err.response?.data?.message || err.message);
      setStep('error');
    }
  };

  const pollNegotiation = async (negId) => {
    let attempts = 0;
    const maxAttempts = 30;
    const interval = setInterval(async () => {
      attempts++;
      try {
        const res = await proxy(`/api/management/v2/contractnegotiations/${negId}`);
        const state = res.data?.['edc:state'] || res.data?.state;
        if (['FINALIZED', 'AGREED', 'VERIFIED'].includes(state)) {
          const agId = res.data?.['edc:contractAgreementId'] || res.data?.contractAgreementId;
          setAgreementId(agId);
          clearInterval(interval);
          setStep('transferring');
        } else if (['TERMINATED', 'ERROR'].includes(state)) {
          clearInterval(interval);
          setErrorMsg(`Negotiation ${state.toLowerCase()}`);
          setStep('error');
        } else if (attempts >= maxAttempts) {
          clearInterval(interval);
          setErrorMsg('Negotiation timed out — check EDC UI for status');
          setStep('error');
        }
      } catch (err) {
        clearInterval(interval);
        setErrorMsg(err.message);
        setStep('error');
      }
    }, 3000);
  };

  const startTransfer = async () => {
    if (!agreementId) return;
    setStep('transferring-running');
    try {
      const dest = destination.trim() || `${window.location.origin}/api/v1/data-exchange/data-receiver`;
      const body = {
        '@context': { 'edc': 'https://w3id.org/edc/v0.0.1/ns/' },
        '@type': 'TransferRequest',
        counterPartyAddress: offer.dsp_endpoint,
        contractId: agreementId,
        assetId: offer.asset_id,
        protocol: 'dataspace-protocol-http',
        dataDestination: { type: 'HttpData', baseUrl: dest },
        transferType: 'HttpData-PUSH',
      };
      const res = await proxy('/api/management/v2/transferprocesses', 'POST', body);
      const tId = res.data?.['@id'] || res.data?.id;
      setTransferId(tId);
      setStep('done');
    } catch (err) {
      setErrorMsg(err.response?.data?.message || err.message);
      setStep('error');
    }
  };

  const StepBadge = ({ n, active, done }) => (
    <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0
      ${done ? 'bg-green-500 text-white' : active ? 'bg-blue-500 text-white' : 'bg-gray-700 text-gray-400'}`}>
      {done ? <CheckCircle size={14} /> : n}
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-black/60" />
      <div className="relative bg-gray-900 border border-gray-700 rounded-xl shadow-2xl w-full max-w-lg"
        onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className="flex items-start gap-3 px-6 py-4 border-b border-gray-800">
          <div className="w-9 h-9 rounded-lg bg-blue-900/40 border border-blue-800 flex items-center justify-center flex-shrink-0">
            <Package size={17} className="text-blue-400" />
          </div>
          <div className="min-w-0 flex-1">
            <h2 className="text-sm font-semibold text-white truncate">{offer.asset_name || offer.asset_id}</h2>
            <p className="text-xs text-gray-400">{offer.provider_name} · {offer.dsp_endpoint}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white flex-shrink-0"><X size={18} /></button>
        </div>

        {/* Consumer connector selector */}
        <div className="px-6 pt-4 pb-0">
          <label className="text-xs text-gray-400 block mb-1">Negotiate using consumer connector</label>
          {consumerProfiles.length === 0 ? (
            <p className="text-xs text-red-400">No connector with a consumer EDC URL configured.</p>
          ) : (
            <select
              value={selectedProfileId}
              onChange={e => setSelectedProfileId(e.target.value)}
              disabled={step !== 'idle' && step !== 'error'}
              className="input text-xs py-1.5"
            >
              {consumerProfiles.map(p => (
                <option key={p.id} value={p.id}>{p.name} — {p.consumerEdcUrl}</option>
              ))}
            </select>
          )}
        </div>

        {/* Steps */}
        <div className="px-6 py-5 space-y-5">
          {/* Step 1 */}
          <div className="flex gap-3">
            <StepBadge n={1} active={['idle','negotiating','polling'].includes(step)} done={['transferring','transferring-running','done'].includes(step)} />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-white">Contract Negotiation</p>
              {step === 'negotiating' && (
                <p className="text-xs text-blue-400 flex items-center gap-1 mt-1"><Loader2 size={11} className="animate-spin" /> Sending negotiation request…</p>
              )}
              {step === 'polling' && (
                <p className="text-xs text-blue-400 flex items-center gap-1 mt-1"><Loader2 size={11} className="animate-spin" /> Waiting for FINALIZED… (ID: {negotiationId?.slice(0, 16)}…)</p>
              )}
              {['transferring','transferring-running','done'].includes(step) && (
                <p className="text-xs text-green-400 flex items-center gap-1 mt-1"><CheckCircle size={11} /> Agreement: {agreementId?.slice(0, 24)}…</p>
              )}
            </div>
          </div>

          {/* Step 2 */}
          <div className="flex gap-3">
            <StepBadge n={2} active={['transferring','transferring-running'].includes(step)} done={step === 'done'} />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-white">Data Transfer</p>
              {step === 'transferring' && (
                <div className="mt-2 space-y-2">
                  <label className="text-xs text-gray-400">Destination URL (optional)</label>
                  <input
                    value={destination}
                    onChange={e => setDestination(e.target.value)}
                    placeholder={`${window.location.origin}/api/v1/data-exchange/data-receiver`}
                    className="input text-xs py-1.5"
                  />
                </div>
              )}
              {step === 'transferring-running' && (
                <p className="text-xs text-blue-400 flex items-center gap-1 mt-1"><Loader2 size={11} className="animate-spin" /> Initiating transfer…</p>
              )}
              {step === 'done' && (
                <p className="text-xs text-green-400 flex items-center gap-1 mt-1"><CheckCircle size={11} /> Transfer initiated · ID: {transferId?.slice(0, 24)}…</p>
              )}
            </div>
          </div>

          {step === 'error' && (
            <div className="flex items-start gap-2 bg-red-900/20 border border-red-800 rounded-lg p-3">
              <AlertTriangle size={15} className="text-red-400 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-red-300">{errorMsg}</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-800 flex justify-end gap-2">
          <button onClick={onClose} className="btn btn-secondary text-sm">Close</button>
          {step === 'idle' && (
            <button onClick={startNegotiation} className="btn btn-primary text-sm">
              <ArrowRight size={14} /> Start Negotiation
            </button>
          )}
          {step === 'transferring' && (
            <button onClick={startTransfer} className="btn btn-primary text-sm">
              <ArrowRight size={14} /> Start Transfer
            </button>
          )}
          {step === 'error' && (
            <button onClick={() => { setStep('idle'); setErrorMsg(''); }} className="btn btn-primary text-sm">
              Retry
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Add Participant modal ──────────────────────────────────────────────────────
function AddParticipantModal({ onClose, onAdded }) {
  const [name, setName] = useState('');
  const [dspEndpoint, setDspEndpoint] = useState('');
  const [participantId, setParticipantId] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const submit = async () => {
    if (!name.trim() || !dspEndpoint.trim()) { setError('Name and DSP Endpoint are required'); return; }
    setLoading(true);
    setError('');
    try {
      await apiClient.post('/data-pool/participants', {
        name: name.trim(),
        dsp_endpoint: dspEndpoint.trim(),
        participant_id: participantId.trim() || null,
      });
      onAdded();
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-black/60" />
      <div className="relative bg-gray-900 border border-gray-700 rounded-xl shadow-2xl w-full max-w-md"
        onClick={e => e.stopPropagation()}>
        <div className="flex items-center gap-3 px-6 py-4 border-b border-gray-800">
          <Server size={17} className="text-blue-400" />
          <h2 className="text-sm font-semibold text-white">Add Dataspace Participant</h2>
          <button onClick={onClose} className="ml-auto text-gray-400 hover:text-white"><X size={18} /></button>
        </div>
        <div className="px-6 py-5 space-y-4">
          <div>
            <label className="label">Provider Name *</label>
            <input value={name} onChange={e => setName(e.target.value)} className="input" placeholder="e.g. Siemens SEDIMARK" />
          </div>
          <div>
            <label className="label">DSP Endpoint *</label>
            <input value={dspEndpoint} onChange={e => setDspEndpoint(e.target.value)} className="input" placeholder="https://provider.example.com/api/dsp" />
          </div>
          <div>
            <label className="label">Participant ID <span className="text-gray-500">(optional)</span></label>
            <input value={participantId} onChange={e => setParticipantId(e.target.value)} className="input" placeholder="e.g. connector-provider" />
          </div>
          {error && <p className="text-xs text-red-400">{error}</p>}
        </div>
        <div className="px-6 py-4 border-t border-gray-800 flex justify-end gap-2">
          <button onClick={onClose} className="btn btn-secondary text-sm">Cancel</button>
          <button onClick={submit} disabled={loading} className="btn btn-primary text-sm">
            {loading ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
            Add Participant
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function DataPool() {
  const { connectorKey, theme } = useStore();
  const isLight = theme === 'light';
  const profile = useMemo(() => getActiveProfile(), [connectorKey]);

  const [participants, setParticipants] = useState([]);
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [crawling, setCrawling] = useState(false);
  const [crawlError, setCrawlError] = useState('');
  const [lastCrawled, setLastCrawled] = useState(null);

  const [search, setSearch] = useState('');
  const [filterProvider, setFilterProvider] = useState('');
  const [filterContentType, setFilterContentType] = useState('');

  const [selectedOffer, setSelectedOffer] = useState(null);
  const [showAddParticipant, setShowAddParticipant] = useState(false);

  useEffect(() => {
    loadParticipants();
    loadOffers();
    if (profile?.consumerEdcUrl) crawl();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadParticipants = async () => {
    try {
      const res = await apiClient.get('/data-pool/participants');
      setParticipants(res.data || []);
    } catch { /* ignore */ }
  };

  const loadOffers = async () => {
    setLoading(true);
    try {
      const res = await apiClient.get('/data-pool/offers');
      setOffers(res.data || []);
    } catch { /* ignore */ } finally {
      setLoading(false);
    }
  };

  const crawl = async () => {
    if (!profile?.consumerEdcUrl) {
      setCrawlError('No consumer EDC URL configured in active connector profile.');
      return;
    }
    setCrawling(true);
    setCrawlError('');
    try {
      const res = await apiClient.post('/data-pool/crawl', {
        consumerEdcUrl: profile.consumerEdcUrl,
        consumerApiKey: profile.consumerApiKey || profile.providerApiKey || '',
      });
      setOffers(res.data?.offers || []);
      setLastCrawled(new Date());
      if (res.data?.errors?.length) {
        setCrawlError(`${res.data.errors.length} participant(s) failed: ${res.data.errors.map(e => e.participant).join(', ')}`);
      }
    } catch (err) {
      setCrawlError(err.response?.data?.error || err.message);
    } finally {
      setCrawling(false);
    }
  };

  const deleteParticipant = async (id) => {
    try {
      await apiClient.delete(`/data-pool/participants/${id}`);
      setParticipants(prev => prev.filter(p => p.id !== id));
    } catch { /* ignore */ }
  };

  // Derived filter options
  const providerOptions = useMemo(() => [...new Set(offers.map(o => o.provider_name).filter(Boolean))], [offers]);
  const contentTypeOptions = useMemo(() => [...new Set(offers.map(o => o.content_type).filter(Boolean))], [offers]);

  // Filtered offers
  const filtered = useMemo(() => offers.filter(o => {
    const q = search.toLowerCase();
    const matchSearch = !q || (o.asset_name || '').toLowerCase().includes(q)
      || (o.description || '').toLowerCase().includes(q)
      || (o.asset_id || '').toLowerCase().includes(q)
      || (o.provider_name || '').toLowerCase().includes(q);
    const matchProvider = !filterProvider || o.provider_name === filterProvider;
    const matchType = !filterContentType || o.content_type === filterContentType;
    return matchSearch && matchProvider && matchType;
  }), [offers, search, filterProvider, filterContentType]);

  const parseKeywords = (kw) => {
    try { return JSON.parse(kw || '[]'); } catch { return []; }
  };

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Page header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold font-display text-white mb-1">Data Pool</h1>
          <p className="text-gray-400 text-sm">Discover and access data offers from all dataspace participants</p>
        </div>
        <button
          onClick={crawl}
          disabled={crawling || !profile?.consumerEdcUrl}
          className="btn btn-primary flex-shrink-0"
          title={!profile?.consumerEdcUrl ? 'Configure a consumer connector first' : ''}
        >
          <RefreshCw size={15} className={crawling ? 'animate-spin' : ''} />
          {crawling ? 'Refreshing…' : 'Refresh Catalog'}
        </button>
      </div>

      {/* Status bar */}
      {(crawlError || lastCrawled) && (
        <div className={`flex items-center gap-2 text-xs px-4 py-2 rounded-lg ${crawlError
          ? isLight ? 'bg-yellow-50 border border-yellow-300 text-yellow-800' : 'bg-yellow-900/20 border border-yellow-800 text-yellow-300'
          : isLight ? 'bg-green-50 border border-green-300 text-green-800' : 'bg-green-900/20 border border-green-800 text-green-300'}`}>
          {crawlError ? <AlertTriangle size={13} /> : <CheckCircle size={13} />}
          {crawlError || `Last refreshed ${formatTime(lastCrawled)} — ${offers.length} offers from ${participants.length} participant(s)`}
        </div>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-4 gap-6">
        {/* ── Left: Participants panel ── */}
        <div className="xl:col-span-1 space-y-3">
          <div className="card">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-sm font-semibold text-white flex items-center gap-2">
                <Globe size={15} className="text-blue-400" /> Participants
              </h2>
              <button
                onClick={() => setShowAddParticipant(true)}
                className="text-blue-400 hover:text-blue-300 transition-colors"
                title="Add participant"
              >
                <Plus size={16} />
              </button>
            </div>

            {participants.length === 0 ? (
              <div className="text-center py-6">
                <Server size={28} className="mx-auto mb-2 text-gray-600" />
                <p className="text-xs text-gray-500">No participants yet.</p>
                <button onClick={() => setShowAddParticipant(true)} className="text-xs text-blue-400 hover:underline mt-1">Add one</button>
              </div>
            ) : (
              <ul className="space-y-2">
                {participants.map(p => (
                  <li key={p.id} className="flex items-start gap-2 group">
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium text-gray-200 truncate">{p.name}</p>
                      <p className="text-xs text-gray-500 truncate">{p.dsp_endpoint}</p>
                      {p.last_crawled_at && (
                        <p className="text-xs text-gray-600 flex items-center gap-1 mt-0.5">
                          <Clock size={10} /> {formatDate(p.last_crawled_at)}
                        </p>
                      )}
                    </div>
                    <button
                      onClick={() => deleteParticipant(p.id)}
                      className="opacity-0 group-hover:opacity-100 transition-opacity text-gray-500 hover:text-red-400 flex-shrink-0 mt-0.5"
                    >
                      <Trash2 size={13} />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Stats mini-card */}
          <div className="card">
            <div className="grid grid-cols-2 gap-3 text-center">
              <div>
                <p className="text-2xl font-bold text-white">{participants.length}</p>
                <p className="text-xs text-gray-400">Participants</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{offers.length}</p>
                <p className="text-xs text-gray-400">Offers</p>
              </div>
            </div>
          </div>
        </div>

        {/* ── Right: Offers grid ── */}
        <div className="xl:col-span-3 space-y-4">
          {/* Filters */}
          <div className="flex flex-wrap gap-3">
            <div className="relative flex-1 min-w-[200px]">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search assets…"
                className="input pl-9 text-sm"
              />
            </div>
            <div className="relative">
              <Filter size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <select
                value={filterProvider}
                onChange={e => setFilterProvider(e.target.value)}
                className="input pl-8 text-sm appearance-none pr-8 min-w-[160px]"
              >
                <option value="">All providers</option>
                {providerOptions.map(p => <option key={p} value={p}>{p}</option>)}
              </select>
              <ChevronDown size={13} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
            </div>
            <div className="relative">
              <FileType size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <select
                value={filterContentType}
                onChange={e => setFilterContentType(e.target.value)}
                className="input pl-8 text-sm appearance-none pr-8 min-w-[160px]"
              >
                <option value="">All types</option>
                {contentTypeOptions.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
              <ChevronDown size={13} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
            </div>
          </div>

          {/* Offer cards */}
          {loading ? (
            <div className="flex items-center justify-center py-16 text-gray-400">
              <Loader2 size={24} className="animate-spin mr-3" /> Loading offers…
            </div>
          ) : filtered.length === 0 ? (
            <div className="card text-center py-16">
              <Package size={40} className="mx-auto mb-3 text-gray-600" />
              <p className="text-gray-400 font-medium">
                {offers.length === 0 ? 'No offers yet' : 'No offers match your filters'}
              </p>
              {offers.length === 0 && participants.length > 0 && (
                <p className="text-sm text-gray-500 mt-1">
                  Click <strong>Refresh Catalog</strong> to fetch offers from {participants.length} participant(s).
                </p>
              )}
              {offers.length === 0 && participants.length === 0 && (
                <p className="text-sm text-gray-500 mt-1">Add participants first, then refresh the catalog.</p>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {filtered.map(offer => {
                const keywords = parseKeywords(offer.keywords);
                return (
                  <div key={offer.id} className="card flex flex-col gap-3 hover:border-blue-800 transition-colors">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <h3 className="text-sm font-semibold text-white truncate">{offer.asset_name || offer.asset_id}</h3>
                        <p className="text-xs text-gray-500 truncate">{offer.asset_id}</p>
                      </div>
                      {offer.content_type && (
                        <span className="text-xs bg-gray-800 text-gray-300 px-2 py-0.5 rounded-full flex-shrink-0 flex items-center gap-1">
                          <FileType size={10} /> {offer.content_type}
                        </span>
                      )}
                    </div>

                    {offer.description && (
                      <p className="text-xs text-gray-400 line-clamp-2">{offer.description}</p>
                    )}

                    {keywords.length > 0 && (
                      <div className="flex flex-wrap gap-1">
                        {keywords.slice(0, 5).map((kw, i) => (
                          <span key={i} className="keyword-tag text-xs px-2 py-0.5 rounded-full flex items-center gap-1">
                            <Tag size={9} /> {kw}
                          </span>
                        ))}
                        {keywords.length > 5 && (
                          <span className="text-xs text-gray-500">+{keywords.length - 5}</span>
                        )}
                      </div>
                    )}

                    <div className="flex items-center justify-between pt-1 border-t border-gray-800">
                      <div className="flex items-center gap-1.5 text-xs text-gray-400">
                        <Server size={11} />
                        <span className="truncate max-w-[160px]">{offer.provider_name}</span>
                      </div>
                      <button
                        onClick={() => setSelectedOffer(offer)}
                        className="btn btn-primary text-xs py-1 px-3"
                      >
                        Request Access
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {selectedOffer && (
        <NegotiateModal offer={selectedOffer} onClose={() => setSelectedOffer(null)} />
      )}
      {showAddParticipant && (
        <AddParticipantModal
          onClose={() => setShowAddParticipant(false)}
          onAdded={loadParticipants}
        />
      )}
    </div>
  );
}
